/**
 * Question Set Editor — Content + Questions tabs (no embedded Configuration tab)
 */

import * as React from 'react';
import { Badge, cn, createEmptyDocument } from '@ai-content-hub/content-kit';
import {
    type EditTabDescriptor,
    type CompositeQuestionsSidebarRow,
    generateId,
    type ContentEditProps,
    CompositeEditShell,
    CompositeEditContentTab,
    CompositeEditSortableQuestionsLayout,
    orderItemsByQuestionsSidebarRows,
    persistQuestionsSidebarRows,
    reconcileQuestionsSidebarRows,
    resolvedQuestionsSidebarRows,
    useLocale,
} from '@ai-content-hub/content-core';
import {
    PageEdit,
    defaultPageData,
    getCompositeQuestionType,
    type PageElementData,
} from '@ai-content-hub/content-elements';
import type { BlockContentType } from '@ai-content-hub/content-elements';
import {
    defaultQuestionSetData,
    type QuestionSetData,
    type QuestionSetItem,
    type QuestionSetConfig,
    type QuestionSetElementType,
} from './schema';
import { FileText, HelpCircle, Settings } from 'lucide-react';
import map from 'lodash/map';
import merge from 'lodash/merge';

const PAGE_BLOCK_TYPES: BlockContentType[] = [
    'rich-text',
    'image',
    'audio',
    'video',
    'text',
    'office-document',
];

const QUESTION_SET_ALLOWED_TYPE_IDS: QuestionSetElementType[] = [
    'mcq',
    'match',
    'ordering',
    'fitb-dropdown',
    'fitb-type',
];

function compositeQuestionTypeLabel(
    type: QuestionSetElementType,
    t: (key: string, fallback?: string) => string
): string {
    switch (type) {
        case 'mcq':
            return t('multipleChoice');
        case 'match':
            return t('questionTypeMatch');
        case 'ordering':
            return t('questionTypeOrdering');
        case 'fitb-dropdown':
            return t('blankModeDropdown');
        case 'fitb-type':
            return t('fillInTheBlanks');
        default:
            return getCompositeQuestionType(type)?.label ?? type;
    }
}

function createItem(type: QuestionSetElementType): QuestionSetItem {
    const def = getCompositeQuestionType(type);
    if (!def) {
        throw new Error(`Unknown question type: ${type}`);
    }
    return {
        id: generateId(),
        type,
        data: def.createDefaultData(),
    } as QuestionSetItem;
}

export type QuestionSetEditProps = ContentEditProps<QuestionSetData> & {
    config?: QuestionSetConfig;
    className?: string;
};

export const QuestionSetEdit: React.FC<QuestionSetEditProps> = ({
    value: data,
    onChange,
    disabled = false,
    className,
    configSlot,
    errors: _errors,
    config,
    language,
}) => {
    const [activeTab, setActiveTab] = React.useState<'content' | 'questions' | 'config'>('content');

    const { t } = useLocale({ config: config?.i18n, initialLanguage: language });

    const safeData: QuestionSetData = {
        ...defaultQuestionSetData,
        ...data,
        pageContent: {
            ...defaultPageData,
            ...(data?.pageContent ?? {}),
            sections: data?.pageContent?.sections ?? defaultPageData.sections,
        },
        items: data?.items ?? [],
    };

    const items = safeData.items;
    const contentSectionCount = safeData.pageContent.sections?.length ?? 0;

    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [selectedSectionId, setSelectedSectionId] = React.useState<string | null>(null);

    // EAA-2083: only the exercise selection follows `items`; the section selection is left
    // alone. Every sidebar-rows change (typing a section title included) rebuilds `items`
    // as a new array, so with no exercises yet this effect cleared the selected section on
    // the first keystroke and closed its panel. A section that is really removed is
    // deselected by CompositeEditSortableQuestionsLayout itself.
    React.useEffect(() => {
        if (items.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((prev) => {
            if (prev && items.some((i) => i.id === prev)) return prev;
            return items[0].id;
        });
    }, [items]);

    const updateData = (updates: Partial<QuestionSetData>) => {
        onChange({
            ...safeData,
            ...updates,
        });
    };

    const handlePageContentChange = (pageContent: PageElementData) => {
        updateData({ pageContent });
    };

    const addItem = (type: QuestionSetElementType) => {
        if (disabled) return;
        const newItem = createItem(type);
        const nextItems = [...items, newItem];
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextItems, (i) => i.id))
            ),
        });
        setSelectedId(newItem.id);
        setSelectedSectionId(null);
        setActiveTab('questions');
    };

    const removeItem = (itemId: string) => {
        if (disabled) return;
        const nextItems = items.filter((i) => i.id !== itemId);
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextItems, (i) => i.id))
            ),
        });
    };

    const updateItem = (itemId: string, next: QuestionSetItem) => {
        if (disabled) return;
        updateData({
            items: map(items, (it) => (it.id === itemId ? next : it)),
        });
    };

    const handleQuestionsSidebarRowsChange = (rows: CompositeQuestionsSidebarRow[]) => {
        if (disabled) return;
        const nextItems = orderItemsByQuestionsSidebarRows(items, rows);
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(rows),
        });
    };

    const handleAddSidebarSection = () => {
        if (disabled) return;
        const sid = generateId();
        const base = resolvedQuestionsSidebarRows(
            safeData.questionsSidebarRows,
            map(items, (i) => i.id)
        );
        handleQuestionsSidebarRowsChange([
            {
                kind: 'section',
                section: { id: sid, title: '', description: createEmptyDocument() },
            },
            ...base,
        ]);
        setSelectedSectionId(sid);
        setSelectedId(null);
    };

    const sidebarRows = React.useMemo(
        () => resolvedQuestionsSidebarRows(safeData.questionsSidebarRows, map(items, (i) => i.id)),
        [items, safeData.questionsSidebarRows]
    );

    const tabs: EditTabDescriptor[] = [
        { id: 'content', label: t('contentTab'), icon: FileText, badge: contentSectionCount },
        { id: 'questions', label: t('questionsTab'), icon: HelpCircle, badge: items.length },
        ...(configSlot ? [{ id: 'config', label: t('configurationTab'), icon: Settings }] : []),
    ];

    const addQuestionOptions = React.useMemo(
        () =>
            map(QUESTION_SET_ALLOWED_TYPE_IDS, (id) => ({
                id,
                label: compositeQuestionTypeLabel(id, t),
            })),
        [t]
    );

    const renderItemEditor = (item: QuestionSetItem): React.ReactNode => {
        const def = getCompositeQuestionType(item.type);
        if (!def?.EditComponent) {
            return null;
        }
        const C = def.EditComponent;
        const props: Record<string, unknown> = {
            value: item.data,
            onChange: (d: unknown) =>
                updateItem(
                    item.id,
                    { ...item, data: d as QuestionSetItem['data'] } as QuestionSetItem,
                ),
            disabled,
        };
        if (def.editorClassName) {
            props.className = def.editorClassName;
        }
        if (def.defaultConfig != null) {
            props.config = merge({}, def.defaultConfig, config ?? {}) as QuestionSetConfig;
        } else if (config != null) {
            props.config = config;
        }
        if (language != null) {
            props.language = language;
        }
        return <C {...props} />;
    };

    return (
        <CompositeEditShell
            prefix="question-set-edit"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as 'content' | 'questions' | 'config')}
            configSlot={configSlot}
            className={cn('question-set-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'content') {
                    return (
                        <CompositeEditContentTab
                            prefix="question-set-edit"
                            disabled={disabled}
                            title={{
                                id: 'qs-title',
                                label: t('title'),
                                value: safeData.title ?? '',
                                onChange: (v) => updateData({ title: v }),
                                placeholder: t('enterTitle'),
                                required: true,
                            }}
                            titleInputClassName="question-set-edit-title-input"
                            description={{
                                id: 'qs-instructions',
                                label: t('instructionsOptional'),
                                value: safeData.instructions ?? '',
                                onChange: (v) => updateData({ instructions: v }),
                                placeholder: t('enterInstructions'),
                                rows: 3,
                            }}
                            descriptionInputClassName="question-set-edit-instructions-input"
                            page={
                                <PageEdit
                                    value={safeData.pageContent}
                                    onChange={handlePageContentChange}
                                    disabled={disabled}
                                    hideSettings
                                    allowedBlockTypes={PAGE_BLOCK_TYPES}
                                    className="question-set-edit-page-inner"
                                    config={config}
                                    language={language}
                                />
                            }
                        />
                    );
                }
                if (tabId === 'questions') {
                    return (
                        <CompositeEditSortableQuestionsLayout
                            prefix="question-set-edit"
                            listTitle={t('questions')}
                            emptyMessage={t('noQuestionsYet')}
                            items={items}
                            selectedId={selectedId}
                            onSelectId={setSelectedId}
                            selectedSectionId={selectedSectionId}
                            onSelectSectionId={setSelectedSectionId}
                            disabled={disabled}
                            minItemsToKeep={0}
                            sidebarRows={sidebarRows}
                            onSidebarRowsChange={handleQuestionsSidebarRowsChange}
                            onAddSidebarSection={handleAddSidebarSection}
                            addSidebarSectionLabel={t('addSection')}
                            removeSidebarSectionLabel={t('removeSection')}
                            onRemoveItem={removeItem}
                            getItemLabel={(itemId, index) => {
                                const item = items.find((i) => i.id === itemId);
                                if (!item) return '';
                                return `${index + 1}. ${compositeQuestionTypeLabel(item.type, t)}`;
                            }}
                            renderDetailHeader={({ itemId }) => {
                                const item = items.find((i) => i.id === itemId);
                                if (!item) return null;
                                return (
                                    <>
                                        <h3 className="text-lg font-semibold text-foreground">
                                            {t('question')} {items.findIndex((i) => i.id === item.id) + 1}
                                        </h3>
                                        <Badge
                                            variant="outline"
                                            className="border-success/30 bg-success/10"
                                        >
                                            {compositeQuestionTypeLabel(item.type, t)}
                                        </Badge>
                                    </>
                                );
                            }}
                            addButtonLabel={t('addQuestion')}
                            addButtonOptions={addQuestionOptions}
                            addButtonClassName="question-set-edit-add-question"
                            onAddItem={(typeId) => addItem(typeId as QuestionSetElementType)}
                            renderItemDetail={({ itemId }) => {
                                const item = items.find((i) => i.id === itemId);
                                if (!item) {
                                    return null;
                                }
                                return renderItemEditor(item);
                            }}
                            detailContent={null}
                            detailEmptyMessage={t('selectOrAddQuestion')}
                            removeItemLabel={t('removeQuestion')}
                            richTextTranslate={t}
                        />
                    );
                }
                return null;
            }}
        />
    );
};

export default QuestionSetEdit;
